/**
 * Score Trajectory Chart Component
 *
 * Displays an enhanced sparkline showing 6-month score trajectory with:
 * - Start and end values displayed
 * - Category benchmark line
 * - Color-coded based on fund rating
 * - Trend indicators
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { FundRating, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Data point for the trajectory chart
 */
export interface TrajectoryDataPoint {
  /** Date label (e.g., "Jan", "Feb") */
  label: string;
  /** Score value (0-100, derived from percentile) */
  score: number;
  /** Original rank */
  rank: number;
  /** Total funds in category */
  totalFunds: number;
}

/**
 * Props for the ScoreTrajectoryChart component
 */
export interface ScoreTrajectoryChartProps {
  /** Rank history entries (should contain 6+ months of data) */
  rankHistory: RankHistoryEntry[];
  /** Fund's current rating for color coding */
  rating: FundRating;
  /** Category benchmark percentile (0-100, e.g., 50 for median) */
  categoryBenchmark?: number;
  /** Optional title override */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Chart dimensions
 */
const CHART_CONFIG = {
  width: 320,
  height: 120,
  paddingX: 40,
  paddingY: 20,
  strokeWidth: 2.5,
  dotRadius: 4,
  benchmarkDash: "6,4",
} as const;

/**
 * Status colors for different ratings
 */
const RATING_COLORS: Record<FundRating, { stroke: string; fill: string; gradient: string }> = {
  "in-form": {
    stroke: "#10b981", // emerald-500
    fill: "#10b981",
    gradient: "from-emerald-500/20 to-emerald-500/5",
  },
  "on-track": {
    stroke: "#0ea5e9", // sky-500
    fill: "#0ea5e9",
    gradient: "from-sky-500/20 to-sky-500/5",
  },
  "off-track": {
    stroke: "#f59e0b", // amber-500
    fill: "#f59e0b",
    gradient: "from-amber-500/20 to-amber-500/5",
  },
  "out-of-form": {
    stroke: "#f43f5e", // rose-500
    fill: "#f43f5e",
    gradient: "from-rose-500/20 to-rose-500/5",
  },
};

/**
 * Text colors for ratings
 */
const RATING_TEXT_COLORS: Record<FundRating, string> = {
  "in-form": "text-emerald-700 dark:text-emerald-400",
  "on-track": "text-sky-700 dark:text-sky-400",
  "off-track": "text-amber-700 dark:text-amber-400",
  "out-of-form": "text-rose-700 dark:text-rose-400",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert rank to percentile score (inverted so lower rank = higher score)
 */
function rankToScore(rank: number, totalFunds: number): number {
  if (totalFunds === 0) return 50;
  const percentile = (rank / totalFunds) * 100;
  return 100 - percentile; // Invert: top rank = high score
}

/**
 * Format month from ISO date string
 */
function formatMonth(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short" });
}

/**
 * Process rank history into chart data points
 * Takes last 6 months of data
 */
function processRankHistory(rankHistory: RankHistoryEntry[]): TrajectoryDataPoint[] {
  // Sort by date and take last 6 months
  const sorted = [...rankHistory].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const recent = sorted.slice(-6);

  return recent.map((entry) => ({
    label: formatMonth(entry.date),
    score: rankToScore(entry.rank, entry.totalFunds),
    rank: entry.rank,
    totalFunds: entry.totalFunds,
  }));
}

/**
 * Calculate trend direction
 */
function calculateTrend(dataPoints: TrajectoryDataPoint[]): "up" | "down" | "flat" {
  if (dataPoints.length < 2) return "flat";

  const firstHalf = dataPoints.slice(0, Math.floor(dataPoints.length / 2));
  const secondHalf = dataPoints.slice(Math.floor(dataPoints.length / 2));

  const firstAvg = firstHalf.reduce((sum, p) => sum + p.score, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, p) => sum + p.score, 0) / secondHalf.length;

  const diff = secondAvg - firstAvg;
  if (diff > 5) return "up";
  if (diff < -5) return "down";
  return "flat";
}

/**
 * Generate SVG path for line chart
 */
function generateLinePath(
  points: TrajectoryDataPoint[],
  chartWidth: number,
  chartHeight: number
): string {
  if (points.length === 0) return "";

  const { paddingX, paddingY } = CHART_CONFIG;
  const plotWidth = chartWidth - paddingX * 2;
  const plotHeight = chartHeight - paddingY * 2;

  const minScore = Math.min(...points.map((p) => p.score));
  const maxScore = Math.max(...points.map((p) => p.score));
  const scoreRange = maxScore - minScore || 20; // Minimum range for flat lines

  // Add 10% padding to score range
  const paddedMin = Math.max(0, minScore - scoreRange * 0.1);
  const paddedMax = Math.min(100, maxScore + scoreRange * 0.1);
  const paddedRange = paddedMax - paddedMin;

  const pathPoints = points.map((point, index) => {
    const x = paddingX + (index / (points.length - 1)) * plotWidth;
    const y = paddingY + ((paddedMax - point.score) / paddedRange) * plotHeight;
    return { x, y };
  });

  // Create smooth bezier curve
  let path = `M ${pathPoints[0]?.x ?? 0},${pathPoints[0]?.y ?? 0}`;

  for (let i = 1; i < pathPoints.length; i++) {
    const prev = pathPoints[i - 1];
    const curr = pathPoints[i];
    if (!prev || !curr) continue;

    // Calculate control points for smooth curve
    const tension = 0.3;
    const prevPrev = pathPoints[i - 2] || prev;
    const next = pathPoints[i + 1] || curr;

    const cp1x = prev.x + (curr.x - prevPrev.x) * tension;
    const cp1y = prev.y + (curr.y - prevPrev.y) * tension;
    const cp2x = curr.x - (next.x - prev.x) * tension;
    const cp2y = curr.y - (next.y - prev.y) * tension;

    path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
  }

  return path;
}

/**
 * Generate SVG path for area fill under the line
 */
function generateAreaPath(
  points: TrajectoryDataPoint[],
  chartWidth: number,
  chartHeight: number
): string {
  if (points.length === 0) return "";

  const linePath = generateLinePath(points, chartWidth, chartHeight);
  const { paddingX, paddingY } = CHART_CONFIG;
  const plotWidth = chartWidth - paddingX * 2;
  const plotHeight = chartHeight - paddingY * 2;

  const lastX = paddingX + plotWidth;
  const bottomY = paddingY + plotHeight;
  const firstX = paddingX;

  return `${linePath} L ${lastX},${bottomY} L ${firstX},${bottomY} Z`;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Start and end value labels
 */
function ValueLabels({
  dataPoints,
  rating,
}: {
  dataPoints: TrajectoryDataPoint[];
  rating: FundRating;
}) {
  if (dataPoints.length < 2) return null;

  const startPoint = dataPoints[0];
  const endPoint = dataPoints[dataPoints.length - 1];

  if (!startPoint || !endPoint) return null;

  const textColor = RATING_TEXT_COLORS[rating];
  const diff = endPoint.score - startPoint.score;

  return (
    <div className="flex items-center justify-between text-sm mb-2">
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">{startPoint.label}</span>
        <span className="font-medium">#{startPoint.rank}</span>
      </div>
      <div className="flex items-center gap-1.5">
        {diff > 0 ? (
          <TrendingUp className={cn("h-4 w-4", textColor)} />
        ) : diff < 0 ? (
          <TrendingDown className={cn("h-4 w-4", textColor)} />
        ) : (
          <Minus className="h-4 w-4 text-muted-foreground" />
        )}
        <span className={cn("text-xs font-medium", textColor)}>
          {diff > 0 ? "+" : ""}
          {diff.toFixed(0)} pts
        </span>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-xs text-muted-foreground">{endPoint.label}</span>
        <span className={cn("font-semibold", textColor)}>#{endPoint.rank}</span>
      </div>
    </div>
  );
}

/**
 * Trend indicator badge
 */
function TrendBadge({
  trend,
  rating,
}: {
  trend: "up" | "down" | "flat";
  rating: FundRating;
}) {
  const textColor = RATING_TEXT_COLORS[rating];

  const trendConfig = {
    up: { icon: TrendingUp, label: "Improving", color: "text-emerald-600" },
    down: { icon: TrendingDown, label: "Declining", color: "text-rose-600" },
    flat: { icon: Minus, label: "Stable", color: "text-muted-foreground" },
  };

  const config = trendConfig[trend];
  const Icon = config.icon;

  return (
    <div className={cn("flex items-center gap-1 text-xs", config.color)}>
      <Icon className="h-3 w-3" />
      <span>{config.label}</span>
    </div>
  );
}

/**
 * SVG Sparkline Chart
 */
function SparklineChart({
  dataPoints,
  rating,
  categoryBenchmark,
}: {
  dataPoints: TrajectoryDataPoint[];
  rating: FundRating;
  categoryBenchmark?: number;
}) {
  const { width, height, paddingX, paddingY, strokeWidth, dotRadius, benchmarkDash } =
    CHART_CONFIG;
  const colors = RATING_COLORS[rating];

  if (dataPoints.length < 2) {
    return (
      <div
        className="flex items-center justify-center bg-muted/50 rounded-lg"
        style={{ width, height }}
      >
        <span className="text-sm text-muted-foreground">Insufficient data</span>
      </div>
    );
  }

  const plotWidth = width - paddingX * 2;
  const plotHeight = height - paddingY * 2;

  // Calculate score range for positioning
  const minScore = Math.min(...dataPoints.map((p) => p.score));
  const maxScore = Math.max(...dataPoints.map((p) => p.score));
  const scoreRange = maxScore - minScore || 20;
  const paddedMin = Math.max(0, minScore - scoreRange * 0.1);
  const paddedMax = Math.min(100, maxScore + scoreRange * 0.1);
  const paddedRange = paddedMax - paddedMin;

  // Calculate benchmark Y position
  const benchmarkY =
    categoryBenchmark !== undefined
      ? paddingY + ((paddedMax - (100 - categoryBenchmark)) / paddedRange) * plotHeight
      : null;

  // Calculate dot positions
  const dotPositions = dataPoints.map((point, index) => ({
    x: paddingX + (index / (dataPoints.length - 1)) * plotWidth,
    y: paddingY + ((paddedMax - point.score) / paddedRange) * plotHeight,
    score: point.score,
    label: point.label,
    rank: point.rank,
  }));

  const linePath = generateLinePath(dataPoints, width, height);
  const areaPath = generateAreaPath(dataPoints, width, height);

  return (
    <div className="relative">
      <svg
        width={width}
        height={height}
        className="overflow-visible"
        viewBox={`0 0 ${width} ${height}`}
        aria-label="Score trajectory chart"
      >
        {/* Gradient definition for area fill */}
        <defs>
          <linearGradient id={`area-gradient-${rating}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.fill} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colors.fill} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Background grid lines */}
        <line
          x1={paddingX}
          y1={paddingY}
          x2={paddingX}
          y2={height - paddingY}
          stroke="currentColor"
          strokeOpacity={0.1}
        />
        <line
          x1={paddingX}
          y1={height - paddingY}
          x2={width - paddingX}
          y2={height - paddingY}
          stroke="currentColor"
          strokeOpacity={0.1}
        />

        {/* Category benchmark line */}
        {benchmarkY !== null && (
          <g>
            <line
              x1={paddingX}
              y1={benchmarkY}
              x2={width - paddingX}
              y2={benchmarkY}
              stroke="#9ca3af"
              strokeWidth={1}
              strokeDasharray={benchmarkDash}
              strokeOpacity={0.6}
            />
            <text
              x={width - paddingX + 4}
              y={benchmarkY + 3}
              fontSize="10"
              fill="#9ca3af"
              className="font-medium"
            >
              Avg
            </text>
          </g>
        )}

        {/* Area fill under the line */}
        <path d={areaPath} fill={`url(#area-gradient-${rating})`} />

        {/* Main trajectory line */}
        <path
          d={linePath}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data point dots */}
        {dotPositions.map((pos, index) => (
          <g key={index}>
            {/* Outer ring for start and end points */}
            {(index === 0 || index === dotPositions.length - 1) && (
              <circle
                cx={pos.x}
                cy={pos.y}
                r={dotRadius + 2}
                fill="none"
                stroke={colors.stroke}
                strokeWidth={1}
                strokeOpacity={0.4}
              />
            )}
            {/* Main dot */}
            <circle
              cx={pos.x}
              cy={pos.y}
              r={index === 0 || index === dotPositions.length - 1 ? dotRadius : dotRadius - 1}
              fill={index === dotPositions.length - 1 ? colors.fill : "white"}
              stroke={colors.stroke}
              strokeWidth={index === 0 || index === dotPositions.length - 1 ? 2 : 1.5}
            />
          </g>
        ))}

        {/* Month labels on x-axis */}
        {dotPositions.map((pos, index) => (
          <text
            key={`label-${index}`}
            x={pos.x}
            y={height - 4}
            textAnchor="middle"
            fontSize="9"
            fill="currentColor"
            fillOpacity={0.5}
          >
            {dataPoints[index]?.label}
          </text>
        ))}
      </svg>
    </div>
  );
}

/**
 * Legend for the chart
 */
function ChartLegend({ categoryBenchmark }: { categoryBenchmark?: number }) {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-0.5 bg-current rounded" />
        <span>Rank Trajectory</span>
      </div>
      {categoryBenchmark !== undefined && (
        <div className="flex items-center gap-1.5">
          <div
            className="w-4 h-0 border-t border-dashed border-gray-400"
            style={{ borderWidth: 1 }}
          />
          <span>Category Avg</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Score Trajectory Chart
 *
 * Displays an enhanced sparkline showing the fund's 6-month rank trajectory
 * with start/end values, category benchmark, and trend indicators.
 *
 * @example
 * ```tsx
 * <ScoreTrajectoryChart
 *   rankHistory={[
 *     { date: "2024-07-01", rank: 15, totalFunds: 50, rating: "on-track" },
 *     { date: "2024-08-01", rank: 12, totalFunds: 50, rating: "on-track" },
 *     { date: "2024-09-01", rank: 8, totalFunds: 50, rating: "in-form" },
 *     { date: "2024-10-01", rank: 6, totalFunds: 50, rating: "in-form" },
 *     { date: "2024-11-01", rank: 5, totalFunds: 50, rating: "in-form" },
 *     { date: "2024-12-01", rank: 3, totalFunds: 50, rating: "in-form" },
 *   ]}
 *   rating="in-form"
 *   categoryBenchmark={50}
 * />
 * ```
 */
export function ScoreTrajectoryChart({
  rankHistory,
  rating,
  categoryBenchmark = 50,
  title = "Score Trajectory",
  className,
}: ScoreTrajectoryChartProps) {
  const dataPoints = processRankHistory(rankHistory);
  const trend = calculateTrend(dataPoints);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <TrendBadge trend={trend} rating={rating} />
        </div>
      </CardHeader>
      <CardContent>
        {/* Start/End Value Labels */}
        <ValueLabels dataPoints={dataPoints} rating={rating} />

        {/* Sparkline Chart */}
        <div className="flex justify-center">
          <SparklineChart
            dataPoints={dataPoints}
            rating={rating}
            categoryBenchmark={categoryBenchmark}
          />
        </div>

        {/* Legend */}
        <ChartLegend categoryBenchmark={categoryBenchmark} />
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for ScoreTrajectoryChart
 */
export function ScoreTrajectoryChartSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
      </CardHeader>
      <CardContent>
        {/* Value Labels Skeleton */}
        <div className="flex items-center justify-between mb-2">
          <div className="space-y-1">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-6" />
          </div>
          <Skeleton className="h-4 w-16" />
          <div className="space-y-1">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-6" />
          </div>
        </div>

        {/* Chart Skeleton */}
        <div className="flex justify-center">
          <Skeleton className="rounded-lg" style={{ width: 320, height: 120 }} />
        </div>

        {/* Legend Skeleton */}
        <div className="flex items-center gap-4 mt-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

export default ScoreTrajectoryChart;
